export interface ScreenLayout {
  x: number;
  y: number;
  w: number;
  h: number;
  scale: number;
  compact: boolean;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function computeScreenLayout(width: number, height: number): ScreenLayout {
  const safeW = Math.max(1, width);
  const safeH = Math.max(1, height);
  const compact = safeW < 700 || safeH < 430;
  const scale = compact ? Math.max(0.9, Math.min(1.15, safeW / 390)) : Math.max(1, Math.min(1.25, safeH / 720));
  return {
    x: 0,
    y: 0,
    w: safeW,
    h: safeH,
    scale,
    compact,
  };
}

export function computeActionButtonRect(layout: ScreenLayout): Rect {
  const size = layout.compact ? 58 : 66;
  return {
    x: layout.x + layout.w - size - 18,
    y: layout.y + layout.h - size - 18,
    w: size,
    h: size,
  };
}

export function computeBuildButtonRects(layout: ScreenLayout): Record<'build' | 'tech', Rect> {
  const w = layout.compact ? 76 : 104;
  const h = layout.compact ? 42 : 46;
  const gap = 8;
  const action = computeActionButtonRect(layout);
  if (layout.compact) {
    const x = layout.x + layout.w - w - 18;
    return {
      build: { x, y: action.y - h * 2 - gap - 10, w, h },
      tech: { x, y: action.y - h - 10, w, h },
    };
  }
  const groupW = w * 2 + gap;
  const x0 = Math.max(layout.x + 118, layout.x + layout.w - groupW - 18);
  const y = action.y - h - 10;
  return {
    build: {
      x: x0,
      y,
      w,
      h,
    },
    tech: {
      x: x0 + w + gap,
      y,
      w,
      h,
    },
  };
}

export function computeInventoryButtonRect(layout: ScreenLayout): Rect {
  const w = layout.compact ? 94 : 112;
  const h = layout.compact ? 42 : 46;
  return {
    x: layout.x + 18,
    y: layout.y + layout.h - h - 20,
    w,
    h,
  };
}

export function computeZonePanelRect(layout: ScreenLayout): Rect {
  const inventory = computeInventoryButtonRect(layout);
  const w = layout.compact ? Math.min(236, layout.w - 124) : 282;
  const h = layout.compact ? 78 : 84;
  return {
    x: layout.x + 18,
    y: Math.max(layout.y + 96, inventory.y - h - 10),
    w,
    h,
  };
}

export function computeInventoryPanelRects(layout: ScreenLayout, slotCount: number): { panel: Rect; close: Rect; slots: Rect[] } {
  const columns = layout.compact ? 5 : 6;
  const slotSize = layout.compact ? 42 : 48;
  const gap = 8;
  const rows = Math.max(1, Math.ceil(slotCount / columns));
  const panelW = Math.min(layout.w - 24, columns * slotSize + (columns - 1) * gap + 36);
  const panelH = Math.min(layout.h - 92, 96 + rows * slotSize + Math.max(0, rows - 1) * gap);
  const panel = {
    x: layout.x + layout.w * 0.5 - panelW * 0.5,
    y: layout.y + Math.max(72, layout.h * 0.5 - panelH * 0.5),
    w: panelW,
    h: panelH,
  };
  const x0 = panel.x + 18;
  const y0 = panel.y + 62;
  const slots: Rect[] = [];
  for (let i = 0; i < slotCount; i++) {
    slots.push({
      x: x0 + (i % columns) * (slotSize + gap),
      y: y0 + Math.floor(i / columns) * (slotSize + gap),
      w: slotSize,
      h: slotSize,
    });
  }
  return {
    panel,
    close: { x: panel.x + panel.w - 88, y: panel.y + 14, w: 70, h: 30 },
    slots,
  };
}

export function computeBuildingPanelRects(layout: ScreenLayout, playerSlotCount = 10, inboxCount = 1, outboxCount = 6, storageCount = 0): {
  panel: Rect;
  deposit: Rect;
  depositAll: Rect;
  collect: Rect;
  remove: Rect;
  close: Rect;
  playerSlots: Rect[];
  inboxSlots: Rect[];
  outboxSlots: Rect[];
  storageSlots: Rect[];
} {
  const slotSize = layout.compact ? 34 : 40;
  const gap = 7;
  const playerColumns = layout.compact ? 5 : 6;
  const playerRows = Math.max(1, Math.ceil(playerSlotCount / playerColumns));
  const machineColumns = layout.compact ? 4 : 6;
  const machineRows = Math.max(1, Math.ceil(Math.max(inboxCount + outboxCount, storageCount) / machineColumns));
  const panelW = Math.min(layout.w - 24, layout.compact ? 372 : 620);
  const panelH = Math.min(layout.h - 84, 156 + Math.max(playerRows, machineRows) * (slotSize + gap) + (layout.compact ? 104 : 120));
  const panel = {
    x: layout.x + layout.w * 0.5 - panelW * 0.5,
    y: layout.y + Math.max(62, layout.h * 0.5 - panelH * 0.5),
    w: panelW,
    h: panelH,
  };
  const buttonH = layout.compact ? 34 : 38;
  const buttonGap = 8;
  const rowY = panel.y + panel.h - buttonH - 14;
  const buttonW = Math.max(62, (panel.w - 28 - buttonGap * 4) / 5);
  const topY = panel.y + 76;
  const playerX = panel.x + 16;
  const machineX = panel.x + panel.w * 0.53;
  const playerSlots = makeSlotRects(playerSlotCount, playerX, topY, playerColumns, slotSize, gap);
  const inboxSlots = makeSlotRects(inboxCount, machineX, topY, machineColumns, slotSize, gap);
  const outputY = topY + (Math.max(1, Math.ceil(inboxCount / machineColumns)) + 1) * (slotSize + gap);
  const outboxSlots = makeSlotRects(outboxCount, machineX, outputY, machineColumns, slotSize, gap);
  const storageSlots = makeSlotRects(storageCount, machineX, topY, machineColumns, slotSize, gap);

  return {
    panel,
    deposit: { x: panel.x + 14, y: rowY, w: buttonW, h: buttonH },
    depositAll: { x: panel.x + 14 + (buttonW + buttonGap), y: rowY, w: buttonW, h: buttonH },
    collect: { x: panel.x + 14 + (buttonW + buttonGap) * 2, y: rowY, w: buttonW, h: buttonH },
    remove: { x: panel.x + 14 + (buttonW + buttonGap) * 3, y: rowY, w: buttonW, h: buttonH },
    close: { x: panel.x + 14 + (buttonW + buttonGap) * 4, y: rowY, w: buttonW, h: buttonH },
    playerSlots,
    inboxSlots,
    outboxSlots,
    storageSlots,
  };
}

export function computeBuildMenuRects(layout: ScreenLayout): {
  panel: Rect;
  options: Rect[];
  close: Rect;
} {
  const panelW = Math.min(layout.w - 24, layout.compact ? 370 : 620);
  const panelH = layout.compact ? 430 : 360;
  const panel = {
    x: layout.x + layout.w * 0.5 - panelW * 0.5,
    y: layout.y + Math.max(78, layout.h * 0.5 - panelH * 0.5),
    w: panelW,
    h: panelH,
  };
  const columns = layout.compact ? 2 : 3;
  const optionW = (panel.w - 28 - (columns - 1) * 10) / columns;
  const optionH = layout.compact ? 92 : 104;
  const optionY = panel.y + 72;
  return {
    panel,
    options: makeGridRects(4, panel.x + 14, optionY, columns, optionW, optionH, 10),
    close: { x: panel.x + panel.w - 88, y: panel.y + panel.h - 44, w: 74, h: 30 },
  };
}

export function computeTechTreeRects(layout: ScreenLayout): { panel: Rect; options: Rect[]; close: Rect } {
  const panelW = Math.min(layout.w - 24, layout.compact ? 372 : 760);
  const panelH = layout.compact ? 520 : 430;
  const panel = {
    x: layout.x + layout.w * 0.5 - panelW * 0.5,
    y: layout.y + Math.max(78, layout.h * 0.5 - panelH * 0.5),
    w: panelW,
    h: panelH,
  };
  const columns = layout.compact ? 2 : 3;
  const optionW = (panel.w - 28 - (columns - 1) * 10) / columns;
  const optionH = layout.compact ? 68 : 84;
  const optionY = panel.y + 72;
  return {
    panel,
    options: makeGridRects(4, panel.x + 14, optionY, columns, optionW, optionH, 10),
    close: { x: panel.x + panel.w - 88, y: panel.y + panel.h - 44, w: 74, h: 30 },
  };
}

export function computeToolsPanelRects(layout: ScreenLayout): { panel: Rect; options: Rect[]; close: Rect } {
  const panelW = Math.min(layout.w - 24, layout.compact ? 372 : 560);
  const panelH = layout.compact ? 360 : 300;
  const panel = {
    x: layout.x + layout.w * 0.5 - panelW * 0.5,
    y: layout.y + Math.max(78, layout.h * 0.5 - panelH * 0.5),
    w: panelW,
    h: panelH,
  };
  const columns = layout.compact ? 1 : 2;
  const optionW = (panel.w - 28 - (columns - 1) * 10) / columns;
  const optionH = layout.compact ? 100 : 126;
  const optionY = panel.y + 74;
  return {
    panel,
    options: makeGridRects(2, panel.x + 14, optionY, columns, optionW, optionH, 10),
    close: { x: panel.x + panel.w - 88, y: panel.y + panel.h - 44, w: 74, h: 30 },
  };
}

export function computeDebugPanelRects(layout: ScreenLayout): { panel: Rect; options: Rect[]; close: Rect } {
  const panelW = Math.min(layout.w - 24, layout.compact ? 372 : 700);
  const panelH = layout.compact ? 520 : 390;
  const panel = {
    x: layout.x + layout.w * 0.5 - panelW * 0.5,
    y: layout.y + Math.max(72, layout.h * 0.5 - panelH * 0.5),
    w: panelW,
    h: panelH,
  };
  const columns = layout.compact ? 2 : 4;
  const optionW = (panel.w - 28 - (columns - 1) * 10) / columns;
  const optionH = layout.compact ? 88 : 104;
  const optionY = panel.y + 74;
  return {
    panel,
    options: makeGridRects(6, panel.x + 14, optionY, columns, optionW, optionH, 10),
    close: { x: panel.x + panel.w - 88, y: panel.y + panel.h - 44, w: 74, h: 30 },
  };
}

function makeSlotRects(count: number, x0: number, y0: number, columns: number, size: number, gap: number): Rect[] {
  const slots: Rect[] = [];
  for (let i = 0; i < count; i++) {
    slots.push({
      x: x0 + (i % columns) * (size + gap),
      y: y0 + Math.floor(i / columns) * (size + gap),
      w: size,
      h: size,
    });
  }
  return slots;
}

function makeGridRects(count: number, x0: number, y0: number, columns: number, width: number, height: number, gap: number): Rect[] {
  const slots: Rect[] = [];
  for (let i = 0; i < count; i++) {
    slots.push({
      x: x0 + (i % columns) * (width + gap),
      y: y0 + Math.floor(i / columns) * (height + gap),
      w: width,
      h: height,
    });
  }
  return slots;
}

export function contains(rect: Rect, x: number, y: number): boolean {
  return x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h;
}
